"use client"

import { useEffect, useState, useRef } from "react"
import Image from "next/image"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useLanguage } from "@/lib/i18n"

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger)
}

export function AboutSection() {
  const { t, locale } = useLanguage()
  const sectionRef = useRef<HTMLElement>(null)
  const photoRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const statsRef = useRef<HTMLDivElement>(null)
  const [scrollProgress, setScrollProgress] = useState(0)

  const [stats, setStats] = useState({ years: 0, projects: 0, companies: 0 })

  // GSAP ScrollTrigger animations
  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      // Photo animation - blur reveal with scale
      if (photoRef.current) {
        gsap.fromTo(
          photoRef.current,
          { opacity: 0, scale: 0.9, filter: "blur(20px)" },
          {
            opacity: 1,
            scale: 1,
            filter: "blur(0px)",
            duration: 1.2,
            ease: "power3.out",
            scrollTrigger: {
              trigger: photoRef.current,
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
          }
        )
      }

      // Content animation - slide in from right
      const contentElements = contentRef.current?.querySelectorAll(".content-item")
      if (contentElements) {
        gsap.fromTo(
          contentElements,
          { opacity: 0, x: 40 },
          {
            opacity: 1,
            x: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: contentRef.current,
              start: "top 75%",
              toggleActions: "play none none reverse",
            },
          }
        )
      }

      // Stats counter animation
      if (statsRef.current) {
        ScrollTrigger.create({
          trigger: statsRef.current,
          start: "top 80%",
          onEnter: () => {
            const duration = 1500
            const steps = 60
            const increment = duration / steps

            let currentStep = 0
            const timer = setInterval(() => {
              currentStep++
              const progress = currentStep / steps

              setStats({
                years: Math.floor(15 * progress),
                projects: Math.floor(100 * progress),
                companies: Math.floor(75 * progress),
              })

              if (currentStep >= steps) {
                clearInterval(timer)
                setStats({ years: 15, projects: 100, companies: 75 })
              }
            }, increment)
          },
          once: true,
        })
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return

      const rect = sectionRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight

      // Calculate progress: 0 when section enters bottom of viewport, 1 when it exits top
      const progress = Math.max(
        0,
        Math.min(1, (windowHeight - rect.top) / (windowHeight + rect.height))
      )
      setScrollProgress(progress)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll() // Initial call

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <section
      ref={sectionRef}
      id="about"
      data-section="about"
      className="py-24 md:py-32 px-6 md:px-8 lg:px-12 bg-[var(--color-bg-primary)] relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none opacity-20">
        {/* Large circle - moves slowly (0.3x speed) */}
        <div
          className="absolute w-64 h-64 border border-[var(--color-accent-gold)] rounded-full"
          style={{
            top: "10%",
            right: "-5%",
            transform: `translate(0, ${scrollProgress * -100 * 0.3}px)`,
            transition: "transform 0.1s linear",
          }}
        />

        {/* Medium square - moves medium speed (0.5x speed) */}
        <div
          className="absolute w-32 h-32 border border-[var(--color-accent-teal)]"
          style={{
            bottom: "20%",
            left: "5%",
            transform: `translate(0, ${scrollProgress * -100 * 0.5}px) rotate(${scrollProgress * 15}deg)`,
            transition: "transform 0.1s linear",
          }}
        />

        {/* Small circle - moves fastest (0.8x speed) */}
        <div
          className="absolute w-16 h-16 bg-[var(--color-accent-copper)] rounded-full opacity-30"
          style={{
            top: "60%",
            right: "15%",
            transform: `translate(0, ${scrollProgress * -100 * 0.8}px)`,
            transition: "transform 0.1s linear",
          }}
        />

        {/* Dots pattern - moves slow (0.4x speed) */}
        <div
          className="absolute flex gap-3"
          style={{
            top: "40%",
            left: "-2%",
            transform: `translate(0, ${scrollProgress * -100 * 0.4}px)`,
            transition: "transform 0.1s linear",
          }}
        >
          <div className="w-2 h-2 bg-[var(--color-accent-teal)] rounded-full opacity-40" />
          <div className="w-2 h-2 bg-[var(--color-accent-teal)] rounded-full opacity-40" />
          <div className="w-2 h-2 bg-[var(--color-accent-teal)] rounded-full opacity-40" />
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* LEFT: Photo */}
          <div ref={photoRef} className="opacity-0 lg:mt-12">
            <div className="relative aspect-[3/4] rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-500">
              <Image
                src="/images/raychard-website-photo.png"
                alt="Raychard Huang - Creative Intelligence Officer"
                fill
                className="object-cover object-center"
                priority
              />
              {/* Subtle geometric overlay accent */}
              <div className="absolute top-4 right-4 w-16 h-16 border-2 border-[var(--color-accent-gold)] rounded-full opacity-30" />
              <div
                className="absolute bottom-4 left-4 w-12 h-12 border-2 border-[var(--color-accent-teal)] opacity-20"
                style={{ transform: "rotate(45deg)" }}
              />
            </div>
          </div>

          {/* RIGHT: Content */}
          <div ref={contentRef}>
            <div className="content-item mb-8 opacity-0">
              <p
                className={`text-2xl md:text-3xl text-[var(--color-accent-gold)] mb-4${locale !== "zh" ? " rotate-[-2deg]" : ""}`}
                style={{ fontFamily: "var(--font-handwritten)" }}
              >
                {t("about.sectionLabel")}
              </p>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium text-[var(--color-text-primary)] leading-tight tracking-tight mb-6">
                {t("about.headline")}
              </h2>
            </div>

            <div ref={statsRef} className="content-item mb-10 pb-10 opacity-0">
              <div className="grid grid-cols-3 gap-8">
                <div className="text-center lg:text-left">
                  <div className="text-4xl md:text-5xl font-medium text-[var(--color-text-primary)] mb-2">
                    {stats.years}+
                  </div>
                  <div className="text-xs uppercase tracking-wider text-[var(--color-text-muted)]">
                    {t("about.statYears")}
                  </div>
                  <div className="h-px w-12 mx-auto lg:mx-0 mt-2 bg-[var(--color-accent-gold)]" />
                </div>

                <div className="text-center lg:text-left">
                  <div className="text-4xl md:text-5xl font-medium text-[var(--color-text-primary)] mb-2">
                    {stats.projects}+
                  </div>
                  <div className="text-xs uppercase tracking-wider text-[var(--color-text-muted)]">
                    {t("about.statProjects")}
                  </div>
                  <div className="h-px w-12 mx-auto lg:mx-0 mt-2 bg-[var(--color-accent-gold)]" />
                </div>

                <div className="text-center lg:text-left">
                  <div className="text-4xl md:text-5xl font-medium text-[var(--color-text-primary)] mb-2">
                    {stats.companies}+
                  </div>
                  <div className="text-xs uppercase tracking-wider text-[var(--color-text-muted)]">
                    {t("about.statCompanies")}
                  </div>
                  <div className="h-px w-12 mx-auto lg:mx-0 mt-2 bg-[var(--color-accent-gold)]" />
                </div>
              </div>
            </div>

            <div className="content-item space-y-6 mb-12 opacity-0">
              <p className="text-base md:text-lg text-[var(--color-text-secondary)] leading-relaxed">
                {t("about.bio1")}
              </p>

              <p className="text-base md:text-lg text-[var(--color-text-secondary)] leading-relaxed">
                {t("about.bio2")}
              </p>

              <p className="text-base md:text-lg text-[var(--color-text-secondary)] leading-relaxed">
                {t("about.bio3")}
              </p>

              <p className="text-base md:text-lg text-[var(--color-text-secondary)] leading-relaxed">
                {t("about.bio4")}
              </p>
            </div>

            <div className="content-item mb-8 opacity-0">
              <p className="text-sm text-[var(--color-text-muted)]">
                {t("about.currentlyAt")}{" "}
                <a
                  href="https://red-peak.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-[var(--color-accent-gold)] font-medium hover:text-[var(--color-accent-teal)] transition-colors"
                >
                  RedPeak
                </a>
                {t("about.partOf")}{" "}
                <a
                  href="https://kyu.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-[var(--color-accent-gold)] font-medium hover:text-[var(--color-accent-teal)] transition-colors"
                >
                  kyu Collective
                </a>
              </p>
            </div>

            <div className="content-item opacity-0">
              <p className="handwritten text-lg md:text-xl text-[var(--color-accent-teal)]/70">
                {t("about.beyond")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
